import http, { endpoints } from '../http';
import { mapBookingFromDto, mapCallProposalFromDto, mapPublicBookingFromCompatDto } from './helpers';
const efktClient = http.createClient(endpoints.efkt);
/**
 * Every method here is real and documented per method; nothing is EFKT-MOCK. The last two — the
 * delegating arms of the book-photographer flow — landed with AXIT-611 (retail-core#3824), which
 * also settled that `updateBookingType` is **not** being built: datahub's only writes a sentence into
 * FLS's `CallInfo2` that nothing reads back, so there was never anything for Efkt to mirror.
 *
 * `getBookingById` backs the public `/booking?id=` page and takes a sales **order** id despite its
 * name — see the method. It is anonymous in effect rather than by any decision taken here: that page
 * never has a token, so `efktClient`'s interceptor attaches none, and whether a route tolerates that
 * is a backend decision per route.
 *
 * There is deliberately no booking-id-from-order-id read here. Datahub has one
 * (`GET /Bookings/{id}/task`) and the public page calls it on that arm, but Efkt never shipped a twin
 * — retail-core#3744 built one and was closed as redundant, because `booking-info` above already
 * answers with the booking id, and with its status besides.
 */
export default class BookingsApi {
    /** `GET /api/cases/{caseId}/bookings` — every booking across every order of the case, flat. */
    static async getCaseBookings(caseId) {
        const response = await efktClient.get(`/api/cases/${encodeURIComponent(caseId)}/bookings`);
        return response.data.items.map(mapBookingFromDto);
    }
    /** `GET /api/order/{orderId}/bookings` — note the singular `order` segment, unlike the case route. */
    static async getOrderBookings(orderId) {
        const response = await efktClient.get(`/api/order/${encodeURIComponent(orderId)}/bookings`);
        return response.data.items.map(mapBookingFromDto);
    }
    /**
     * REAL — `GET /orders/{salesOrderId}/booking-info`, compat surface. Verified anonymous on stage:
     * 200 with the order's main booking, and **404** (`"Sales order '…' has no main booking."`) for an
     * order Efkt does not own — which is what makes the caller's Efkt-first/datahub-fallback work.
     *
     * **The argument is a sales order id, not a booking id**, despite what the public page calls its
     * `?id=`. The seller link is built as `/booking?id={orderId}`.
     */
    static async getBookingById(salesOrderId) {
        const response = await efktClient.get(`/orders/${encodeURIComponent(salesOrderId)}/booking-info`);
        return response.data ? mapPublicBookingFromCompatDto(response.data, salesOrderId) : undefined;
    }
    /**
     * REAL — `GET /Bookings/{bookingId}/proposals`, the gateway's `Compat: Bookings` surface. Verified
     * on stage 2026-09-03: returns real timeslots, `suggestedProposal` included.
     *
     * The path is datahub's verbatim, capital `B` and no `/api` prefix. It was
     * `GET /api/bookings/{bookingId}/proposals`, which 404s. `ICallProposalDto` needed no change at
     * all — the compat response is datahub's field-for-field (`durationMinutes`, `suggestedProposal`,
     * `proposalList`), which is exactly what this DTO was already written against.
     *
     * The datahub trap carries over unchanged: a `suggestedProposal`-less response resolves to
     * `undefined` rather than an empty proposal set, so every caller renders "no proposal" for that
     * case. Anonymous callers get `502` here — the gateway proxies to a Production API that needs the
     * bearer token — so this is one of the few reads that genuinely requires auth.
     */
    static async getBookingProposal(bookingId) {
        const response = await efktClient.get(`/Bookings/${encodeURIComponent(bookingId)}/proposals`);
        return response.data.suggestedProposal ? mapCallProposalFromDto(response.data) : undefined;
    }
    /**
     * REAL — `POST /Bookings/{bookingId}/proposals/confirm`, `Compat: Bookings`. The request body is
     * `CompatConfirmProposalRequest`, i.e. `{proposalId}`, which `IConfirmBookingProposalRequestDto`
     * already matched. Was `POST /api/bookings/{bookingId}/proposals/confirm`, which 404s.
     *
     * **Always 200**, refusal or not — so the body is the only failure signal there is, and every
     * caller has to read `success`. Returned raw rather than thrown on, matching datahub's
     * `confirmBookingPhotographer`, whose callers already branch on the same field.
     *
     * Not fired during verification — it confirms a real timeslot against the Production API, so it
     * has a side effect that a probe should not cause. Path and body come from the live spec.
     */
    static async confirmBookingProposal(proposal) {
        const body = { proposalId: proposal.id };
        const response = await efktClient.post(`/Bookings/${encodeURIComponent(proposal.bookingId)}/proposals/confirm`, body);
        return response.data;
    }
    /**
     * REAL — `POST /Bookings/third-party-link`, `Compat: Bookings`. Asks that one of the case's own
     * contacts be sent the link for choosing this booking's timeslot.
     *
     * The path is datahub's verbatim, capital `B` and no `/api` prefix, and so is the body — including
     * its `taskId` key, which carries the **booking** id. Was a guessed
     * `POST /api/task/{taskId}/third-party-link`, which never existed.
     *
     * **Always 200**, refusal or not, so the body is the only failure signal there is: an email nobody
     * on the case owns, a booking no longer awaiting a timeslot and an unreachable downstream all
     * arrive as `success: false`. Returned raw rather than thrown on, matching `confirmBookingProposal`
     * above.
     *
     * `success: true` means the request is held and will be acted on, **not** that a message has left;
     * delivery is reported in the notification audit, not here.
     */
    static async sendThirdPartyBookingLink(params) {
        const body = {
            taskId: params.bookingId,
            caseId: params.caseId,
            realtorName: params.realtorName,
            recipientName: params.recipientName,
            recipientPhone: params.recipientPhone,
            recipientEmail: params.recipientEmail,
        };
        const response = await efktClient.post('/Bookings/third-party-link', body);
        return response.data;
    }
    /**
     * REAL — `POST /orders/tasks/{taskId}/partially`, `Compat: Tasks`. Records that the customer
     * declined to pick a time and asked the booking department to arrange one.
     *
     * Datahub used the same route to mark an order task partially complete, which meant exactly this
     * one thing. Efkt keeps no task state, so what is recorded is the request itself — an instruction
     * on the booking. The path segment is therefore a **booking** id despite its name; it lives on
     * this class rather than `TasksApi` for that reason, beside the other delegating arm.
     *
     * **Always 200** — same body-carries-the-refusal contract as above. Idempotent: asking twice
     * succeeds and writes nothing the second time.
     */
    static async recordEsoftArrangement(bookingId) {
        const response = await efktClient.post(`/orders/tasks/${encodeURIComponent(bookingId)}/partially`);
        return response.data;
    }
}
